import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, List, ListItem, ListItemText, CircularProgress, Alert, ListItemButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useLookupProfilesMutation } from '../../../../services/ProfileService';
import { ProfileView } from '../../../../services/ProfileService/types/profileTypes';
import { PageLoading as Loading } from '../../../common/components';

interface SearchUserDialogProps {
    open: boolean;
    onClose: () => void;
    onSearch?: (searchValue: string) => void;
}

export const SearchUserDialog: React.FC<SearchUserDialogProps> = ({ open, onClose, onSearch }) => {
    const [searchValue, setSearchValue] = useState("");
    const [triggerSearch, { data, isLoading, error }] = useLookupProfilesMutation();
    const [searched, setSearched] = useState(false);
    const navigate = useNavigate();

    React.useEffect(() => {
        if (!open) {
            setSearchValue("");
            setSearched(false);
        }
    }, [open]);

    const handleSearch = async (e?: React.MouseEvent | React.KeyboardEvent) => {
        if (e) e.preventDefault();
        if (onSearch) onSearch(searchValue);
        try {
            const response = await triggerSearch({ searchTerm: searchValue });
            if (response.data) {
                setSearched(true);
            } else {
                console.error("No data returned from search API.");
            }
        } catch (err) {
            console.error("Error during search API call:", err);
        }
    };
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && searchValue.trim()) {
            handleSearch(e);
        }
    };
    const handleClose = () => {
        if (onClose) onClose();
    };

    const handleUserClick = (user: ProfileView) => {
        // Navigate to ProfileEdit page with the user's identity
        navigate(`/profile-edit?userName=${encodeURIComponent(user.identity)}`);
        // Close the dialog
        handleClose();
    };
    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>Search Users</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Search Value"
                    type="text"
                    fullWidth
                    value={searchValue}
                    onChange={e => setSearchValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                />
                {isLoading && <CircularProgress sx={{ mt: 2 }} />}
                {error && <Alert severity="error">Failed to search users.</Alert>}
                {searched && data && (
                    <List sx={{ mt: 2 }}>
                        {data.length === 0 && <ListItem><ListItemText primary="No users found." /></ListItem>}
                        {data.map((user: ProfileView) => (
                            <ListItem key={user.identity} disablePadding>
                                <ListItemButton onClick={() => handleUserClick(user)}>
                                    <ListItemText 
                                        primary={user.name} 
                                        secondary={`${user.email} | ${user.identity}`}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
                <Button onClick={handleSearch} variant="contained" disabled={!searchValue.trim()}>Search</Button>
            </DialogActions>
        </Dialog>
    );
};
